import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import client from '../../../lib/feathers';

const OUTCOME_STYLE = {
  ok:      { label: 'Success',  classes: 'bg-green-100 text-green-800' },
  partial: { label: 'Partial',  classes: 'bg-amber-100 text-amber-800' },
  failed:  { label: 'Failed',   classes: 'bg-red-100 text-red-800' },
  skipped: { label: 'Skipped',  classes: 'bg-gray-100 text-gray-800' },
  running: { label: 'Running',  classes: 'bg-blue-100 text-blue-800' },
};

const TRIGGER_LABEL = {
  orchestrator:     'Orchestrator',
  manual:           'Manual',
  c7_autotrigger:   'Commerce7 webhook',
  new_listing_hook: 'New-listing hook',
  rescan:           'Rescan',
};

function OutcomePill({ outcome }) {
  const meta = OUTCOME_STYLE[outcome] || { label: outcome, classes: 'bg-gray-100 text-gray-800' };
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${meta.classes}`}>
      {meta.label}
    </span>
  );
}

function StageChip({ stageKey, stage }) {
  const ok = stage?.ok === true;
  const failed = stage?.ok === false;
  const classes = ok
    ? 'bg-green-50 text-green-700 ring-green-200'
    : failed
    ? 'bg-red-50 text-red-700 ring-red-200'
    : 'bg-gray-50 text-gray-600 ring-gray-200';
  const icon = ok ? '✓' : failed ? '✗' : '–';
  const dur = stage?.durationSec != null ? ` · ${stage.durationSec}s` : '';
  const title = stage?.error || (stage?.stats && JSON.stringify(stage.stats)) || '';
  return (
    <span
      title={title}
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono ring-1 ring-inset ${classes}`}
    >
      <span className="mr-1">{icon}</span>{stageKey}{dur}
    </span>
  );
}

function durationLabel(startedAt, finishedAt) {
  if (!startedAt || !finishedAt) return null;
  const ms = new Date(finishedAt) - new Date(startedAt);
  if (ms < 1000) return `${ms}ms`;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return rem ? `${min}m ${rem}s` : `${min}m`;
}

export default function DataHealthPanel() {
  const listing = useSelector((s) => s.listings.selected);
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [ticking, setTicking] = useState(false);
  const [tickMessage, setTickMessage] = useState(null);
  const [recrawlState, setRecrawlState] = useState('idle'); // idle | enqueuing | queued | error
  const [recrawlMessage, setRecrawlMessage] = useState(null);

  const load = async () => {
    if (!listing?.id) return;
    try {
      const result = await client.service('admin/listing-enrichment-run').find({
        query: { listingId: listing.id, $limit: 10 },
      });
      setRuns(result.data || []);
      setError(null);
    } catch (e) {
      console.error('DataHealth load error', e);
      setError(e.message || 'Failed to load enrichment history');
    }
  };

  // Triggers ONE tick of the spot-enrichment worker against the global
  // pending queue (not scoped to this listing — the worker just picks
  // the highest-priority pending row). For "play with it" testing.
  const runPendingTick = async () => {
    setTicking(true);
    setTickMessage(null);
    try {
      const result = await client.service('admin/enrichment-tick').create({ kind: 'spot' });
      if (result.processed === 0) {
        setTickMessage('Queue is empty — no pending tasks to run.');
      } else {
        setTickMessage(`Ran task for listing ${result.run.listingId.slice(0, 8)}… (${result.run.outcome})`);
      }
      await load();
    } catch (e) {
      setTickMessage(`Tick failed: ${e.message}`);
    } finally {
      setTicking(false);
    }
  };

  // POST a `kind='full'` row at priority=10 against this listing.
  // The cron supervisor's next tick picks it up and runs the full
  // pipeline chain (stage 01 → 02 → 02c → 03 → 04). Duplicate-clicks
  // are caught by the partial unique index → "Already queued".
  const enqueueRecrawl = async () => {
    if (!listing?.id || recrawlState === 'enqueuing') return;
    setRecrawlState('enqueuing');
    setRecrawlMessage(null);
    try {
      await client.service('admin/listing-enrichment-run').create({
        listingId: listing.id,
        kind: 'full',
        triggeredBy: 'rescan',
        outcome: 'pending',
        priority: 10,
      });
      setRecrawlState('queued');
      setRecrawlMessage('Queued — full chain runs on next supervisor tick.');
      await load();
    } catch (e) {
      const msg = e.message || '';
      if (/unique/i.test(msg) || /SequelizeUniqueConstraintError/.test(msg)) {
        setRecrawlState('queued');
        setRecrawlMessage('Already queued for a full re-crawl.');
      } else {
        setRecrawlState('error');
        setRecrawlMessage(`Enqueue failed: ${msg}`);
      }
    }
  };

  useEffect(() => {
    load();
  }, [listing?.id]);

  const latest = runs && runs[0];
  const history = runs ? runs.slice(1) : [];
  const stages = latest?.stages && typeof latest.stages === 'object' ? latest.stages : null;
  const stageKeys = stages ? Object.keys(stages).sort() : [];

  return (
    <div className="bg-white shadow overflow-hidden px-4 py-5 sm:rounded-lg sm:p-6">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Pipeline Data Health
        </h3>
        <div className="flex gap-3 items-center">
          {(tickMessage || recrawlMessage) && (
            <span className="text-xs text-gray-600 italic">{recrawlMessage || tickMessage}</span>
          )}
          <button
            type="button"
            onClick={enqueueRecrawl}
            disabled={recrawlState === 'enqueuing' || recrawlState === 'queued'}
            className="text-sm px-3 py-1 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            title="Queue a full pipeline re-crawl for this listing (priority 10 — jumps ahead of background sweep)"
          >
            {recrawlState === 'enqueuing' ? 'Queuing…' :
             recrawlState === 'queued' ? 'Queued ✓' : 'Re-crawl listing'}
          </button>
          <button
            type="button"
            onClick={runPendingTick}
            disabled={ticking}
            className="text-sm px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            title="Pop one pending spot-enrichment task from the queue and run it"
          >
            {ticking ? 'Running…' : 'Run pending tasks'}
          </button>
          <button
            type="button"
            onClick={load}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
        {error && (
          <div className="text-sm text-red-700">{error}</div>
        )}

        {!error && runs === null && (
          <div className="text-sm text-gray-500">Loading…</div>
        )}

        {!error && runs && runs.length === 0 && (
          <div className="text-sm text-gray-500 italic">
            No pipeline runs recorded for this listing yet.
          </div>
        )}

        {!error && latest && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <OutcomePill outcome={latest.outcome} />
              <div className="text-sm text-gray-900">
                {latest.startedAt
                  ? <>Last run {formatDistanceToNow(new Date(latest.startedAt), { addSuffix: true })}</>
                  : <>Queued {latest.createdAt ? formatDistanceToNow(new Date(latest.createdAt), { addSuffix: true }) : 'just now'}</>}
              </div>
              <div className="text-xs text-gray-500">
                {TRIGGER_LABEL[latest.triggeredBy] || latest.triggeredBy}
                {durationLabel(latest.startedAt, latest.finishedAt)
                  ? ` · ${durationLabel(latest.startedAt, latest.finishedAt)}`
                  : ''}
              </div>
            </div>

            {latest.outcome === 'awaiting_review' && !latest.approvedAt && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center justify-between">
                <div className="text-sm text-amber-900">
                  <strong>Proposal ready for review.</strong> Stages 03 + 04 ran in dry-run; nothing has been written to prod yet.
                </div>
                <NavLink
                  to="/admin/reports/listing-data-health/pending-approval"
                  className="inline-flex items-center px-3 py-1.5 bg-primary text-white rounded-md text-sm font-medium hover:bg-red-700 whitespace-nowrap ml-3"
                >
                  Review proposal →
                </NavLink>
              </div>
            )}

            {stageKeys.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {stageKeys.map((k) => (
                  <StageChip key={k} stageKey={k} stage={stages[k]} />
                ))}
              </div>
            )}

            {latest.error && (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-xs font-mono text-red-800 whitespace-pre-wrap break-all">
                {latest.error}
              </div>
            )}
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setShowHistory(!showHistory)}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              {showHistory ? 'Hide' : 'Show'} history ({history.length})
            </button>
            {showHistory && (
              <table className="mt-3 min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase text-xs tracking-wider">When</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase text-xs tracking-wider">Trigger</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase text-xs tracking-wider">Outcome</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase text-xs tracking-wider">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {history.map((r) => {
                    const whenTs = r.startedAt || r.createdAt;
                    return (
                    <tr key={r.id}>
                      <td className="px-3 py-2 text-gray-700" title={whenTs ? format(new Date(whenTs), 'PPpp') : ''}>
                        {whenTs ? formatDistanceToNow(new Date(whenTs), { addSuffix: true }) : '—'}
                        {!r.startedAt && <span className="text-xs text-gray-400 ml-1">(queued)</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {TRIGGER_LABEL[r.triggeredBy] || r.triggeredBy}
                      </td>
                      <td className="px-3 py-2"><OutcomePill outcome={r.outcome} /></td>
                      <td className="px-3 py-2 text-gray-700 font-mono text-xs">
                        {durationLabel(r.startedAt, r.finishedAt) || '—'}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
